// A stored description as the words a bound paragraph draws: tags dropped, the entities a rich-text editor or a
// Shopify export writes ("&amp;", "&nbsp;", "&#39;") read as the characters they stand for, spaces collapsed. A
// card or showcase that has room for a line or two takes the summary: the opening sentences, cut at a word.

/** The named entities descriptions carry, and the characters they stand for. */
const NAMED: Readonly<Record<string, string>> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“',
  hellip: '…', ndash: '–', mdash: '—', eacute: 'é', egrave: 'è', agrave: 'à', ccedil: 'ç', ecirc: 'ê', euro: '€'
};

/** The longest summary, in characters, before it is cut at a word. */
const SUMMARY_LIMIT = 160;

/** A description's opening title: a bold or heading line on its own, as Shopify descriptions often begin. */
const LEADING_TITLE_RE = /^\s*(?:<p[^>]*>\s*)?<(strong|b|h[1-6])\b[^>]*>[\s\S]*?<\/\1>\s*(?:<br\s*\/?>|<\/p>)/i;

// Reads the entities in a text as their characters, leaving any it does not know as written.
export const decodeEntities = (text: string): string => text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, code: string) => {
  if (code[0] !== '#') return NAMED[code.toLowerCase()] ?? whole;
  const point = code[1] === 'x' || code[1] === 'X' ? parseInt(code.slice(2), 16) : Number(code.slice(1));
  return Number.isFinite(point) && point > 0 && point < 0x110000 ? String.fromCodePoint(point) : whole;
});

// Turns stored HTML into the plain words a paragraph draws.
export const plainText = (html: string): string => decodeEntities(html.replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim();

// Gives a description's opening after its title line, whole sentences while they fit, else cut at a word with an ellipsis.
export function summaryOf(html: string): string {
  const words = plainText(html.replace(LEADING_TITLE_RE, '')) || plainText(html);
  if (words.length <= SUMMARY_LIMIT) return words;
  const sentences = words.slice(0, SUMMARY_LIMIT).match(/^.*[.!?](?=\s)/)?.[0];
  if (sentences && sentences.length >= SUMMARY_LIMIT / 2) return sentences;
  return `${words.slice(0, SUMMARY_LIMIT).replace(/\s+\S*$/, '')}…`;
}
